<template>
  <div>
    <div v-if="survey" class="survey">
      <SurveyComplete
        v-if="!preview && recipient.completed_at"
        :survey="survey"
        :recipient="recipient"
      ></SurveyComplete>
      <div v-else class="the_survey">
        <div class="survey_heading">
          <h1 v-if="preview" class="survey-ui__title">Previewing Survey</h1>
          <figure
            v-if="survey.svg_logo"
            class="survey-company-logo"
            v-html="survey.svg_logo"
          ></figure>
          <h1 class="survey-ui__title">{{ survey.name }}</h1>
          <div class="survey-powered-by">
            <span class="subheading">
              Powered by:
              <div class="survey-powered-logo">
                <a
                  class="subtle-text-link"
                  href="https://www.userevidence.com?utm_campaign=survey-taking-experience_logo"
                  target="_blank"
                  title="Visit UserEvidence.com"
                  alt="UserEvidence"
                  >UELogo</a
                >
              </div>
            </span>
          </div>
          <!-- We don't show the promotion_text when there is an interstitial -->
          <!-- https://userevidence.slack.com/archives/C02AUUZ8ALR/p1664231383011059 -->
          <div
            v-if="!show_interstital"
            class="survey-description"
            v-html="survey.promotion_text"
          ></div>
        </div>

        <div v-if="expired">
          <h2>This survey is no longer active</h2>
          <div v-html="survey.expired_text"></div>
        </div>

        <div v-else>
          <div v-if="show_interstital" class="interstitial mt-3">
            <div class="mb-5" v-html="survey.custom_survey_landing_page_text"></div>
            <div class="text-center">
              <b-button variant="primary" @click="agreeAndContinue">Agree and take survey</b-button>
            </div>
          </div>

          <div v-else ref="questions" class="questions">
            <div v-if="show_company" class="question">
              <h2 class="big-question-number">{{ company_count }}</h2>
              <div class="flex-fill">
                <h3>About Your Company</h3>
                <CompanyQuestion
                  :recipient="recipient"
                  :preview="preview"
                  @recipient-updated="updateRecipient"
                  :question-flags="companyQuestionFlags"
                ></CompanyQuestion>
              </div>
            </div>

            <div v-if="show_personal" class="question">
              <h2 class="big-question-number">{{ personal_count }}</h2>
              <div class="flex-fill">
                <h3>About You</h3>
                <RecipientQuestion
                  :recipient="recipient"
                  :preview="preview"
                  @recipient-updated="updateRecipient"
                  :question-flags="recipientQuestionFlags"
                ></RecipientQuestion>
              </div>
            </div>

            <div v-for="(question, i) in questions" :key="i" class="question">
              <h2 class="big-question-number">{{ start_count + i }}</h2>
              <div class="flex-fill">
                <h3 v-html="question.the_question"></h3>

                <div v-if="question.type == 'MultipleChoiceMany'" class="mb-0">
                  <MultipleChoiceMany
                    :question="question"
                    @check="saveResponse"
                    @uncheck="removeResponse"
                  ></MultipleChoiceMany>
                </div>

                <div v-else-if="question.type == 'MultipleChoiceOne'" class="mb-0">
                  <MultipleChoiceOne
                    :question="question"
                    @change="saveResponse"
                  ></MultipleChoiceOne>
                </div>

                <div v-else-if="question.type == 'NamedAttribution'" class="mb-0">
                  <MultipleChoiceOne
                    :question="namedAttributionQuestion"
                    @change="saveResponse"
                  ></MultipleChoiceOne>
                </div>

                <div v-else-if="question.type == 'Nps'" class="mb-0 w-100">
                  <Nps :question="question" :response="npsResponse" @change="saveResponse"></Nps>
                </div>

                <div v-else-if="question.type == 'Testimonial'" class="mb-0">
                  <Testimonial :question="question" @change="saveResponse"></Testimonial>
                </div>

                <div v-else-if="question.type == 'ReferenceVolunteer'" class="mb-0">
                  <MultipleChoiceMany
                    :question="question"
                    @check="saveResponse"
                    @uncheck="removeResponse"
                  ></MultipleChoiceMany>
                </div>

                <div v-else-if="question.type == 'Slider'" class="mb-0 w-100">
                  <Slider :question="question" @change="saveResponse"></Slider>
                </div>

                <div v-else-if="question.type == 'Matrixx'" class="mb-0 w-100">
                  <MatrixxQuestion :question="question" @change="saveResponse"></MatrixxQuestion>
                </div>

                <div v-else-if="question.type == 'ClassifierMcoQuestion'" class="mb-0 w-100">
                  <MultipleChoiceOne
                    :question="question"
                    @change="saveResponse"
                  ></MultipleChoiceOne>
                </div>
              </div>
            </div>

            <div class="ue-survey-taking-submit-btn-container">
              <b-button variant="survey-taking" @click="goDone()">Done</b-button>
            </div>
          </div>

          <SurveyFooter :account_name="this.account.name"></SurveyFooter>
        </div>

        <SurveyTakingFab></SurveyTakingFab>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import appsignal from '@lib/appsignal'
import MultipleChoiceMany from '@/survey/components/MultipleChoiceMany.vue'
import MultipleChoiceOne from '@/survey/components/MultipleChoiceOne.vue'
import Slider from '@/survey/components/SliderQuestion.vue'
import Nps from '@/survey/components/Nps.vue'
import Testimonial from '@/survey/components/Testimonial.vue'
import CompanyQuestion from '@/survey/components/CompanyQuestion.vue'
import MatrixxQuestion from '@/survey/components/MatrixxQuestion.vue'
import RecipientQuestion from '@/survey/components/RecipientQuestion.vue'
import SurveyComplete from '@/survey/pages/SurveyComplete.vue'
import SurveyTakingFab from '@/survey/components/SurveyTakingFab.vue'
import SurveyFooter from '@/survey/components/Footer.vue'
import { mapState } from 'pinia'
import { useQuestionStore } from '@app/stores/QuestionStore.ts'
import { useRecipientStore } from '@app/stores/RecipientStore.ts'
import { useSurveyResponseStore } from '@app/stores/SurveyResponseStore.ts'
import { postResponse, patchResponse, deleteResponse } from '@app/api.js'
import {
  getSurveyPreview,
  patchRecipient,
  patchRecipientDone,
  signInAndGetRecipient,
} from '@/survey/api.js'

export default {
  components: {
    MultipleChoiceMany,
    MultipleChoiceOne,
    Nps,
    Testimonial,
    Slider,
    CompanyQuestion,
    RecipientQuestion,
    MatrixxQuestion,
    SurveyComplete,
    SurveyTakingFab,
    SurveyFooter,
  },
  props: {
    recipient_token: { type: String, required: true },
    company_id: { type: Number, required: true },
    preview: { type: Boolean, default: false },
  },
  data() {
    return {
      account: null,
      // these are used to show keep the questions up after they are completed
      company_count: 1,
      survey: null,
      optin: false,
    }
  },
  computed: {
    ...mapState(useQuestionStore, ['upsertQuestionsAndAnswers']),
    ...mapState(useRecipientStore, [
      'recipients',
      'upsertRecipient',
      'upsertRecipientAndResponses',
    ]),
    ...mapState(useSurveyResponseStore, [
      'removeSurveyResponse',
      'surveyResponses',
      'upsertSurveyResponse',
    ]),
    recipientQuestionFlags() {
      // Toggles that determine which recipient questions are shown
      return {
        showRoleQuestion: this.survey.show_role_question,
        showTitleQuestion: this.survey.show_title_question,
        showSeniorityQuestion: this.survey.show_seniority_question,
      }
    },
    companyQuestionFlags() {
      // Toggles that determine which company questions are shown
      return {
        showCompanyNameQuestion: this.survey.show_company_name_question,
        showIndustryQuestion: this.survey.show_industry_question,
        showCompanySizeQuestion: this.survey.show_company_size_question,
      }
    },
    anyCompanyQuestionsVisible() {
      // Check if any of the 3 questions are visible
      return Object.values(this.companyQuestionFlags).some((flag) => flag)
    },
    anyRecipientQuestionsVisible() {
      // Check if any of the 3 questions are visible
      return Object.values(this.recipientQuestionFlags).some((flag) => flag)
    },
    show_interstital() {
      if (this.preview) return false
      return (
        !this.recipient.responded &&
        this.survey.custom_survey_landing_page_text?.length > 0 &&
        !this.optin
      )
    },
    npsResponse() {
      const npsQuestion = this.questions.find((q) => q.type == 'Nps')
      return this.surveyResponses.find((r) => r.question_id == npsQuestion.id)
    },
    recipient() {
      if (this.preview) {
        return {
          id: '1',
          survey_id: this.survey_id,
          first_name: 'Customer',
          title: 'Title',
          email: 'customer@userevidence.com',
          best_company_name: 'Company Name',
          has_company_info: false,
          has_personal_info: false,
        }
      }

      return this.recipients.find((r) => r.token == this.recipient_token)
    },
    questions() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.survey?.questions.sort((a, b) => a.position - b.position)
    },
    questionCount() {
      return this.questions?.length
    },
    namedAttributionQuestion() {
      var q = this.questions.filter((q) => q.type == 'NamedAttribution')[0]
      if (this.survey.custom_named_attribution) return q

      var name = 'Name'
      var title = 'Job Title'
      var company = 'Company Name'

      if (this.recipient.first_name && this.recipient.last_name)
        name = `${this.recipient.first_name} ${this.recipient.last_name}`
      if (this.recipient.best_company_name) company = this.recipient.best_company_name
      else if (this.recipient.company_name) company = this.recipient.company_name
      if (this.recipient.title) title = this.recipient.title

      q.the_question = `Are you willing to let ${this.account.name} use your name and company in published research? Here's how it would show up: "${name}, ${title}, ${company}"`
      return q
    },
    show_company() {
      if (this.survey.nps_survey) return false
      if (this.recipient.has_company_info) return false
      if (!this.anyCompanyQuestionsVisible) return false
      return true
    },
    show_personal() {
      if (this.survey.nps_survey) return false
      if (this.recipient.has_personal_info) return false
      if (!this.anyRecipientQuestionsVisible) return false
      return true
    },
    start_count() {
      let start = 1
      if (this.show_company) start++
      if (this.show_personal) start++
      return start
    },
    personal_count() {
      if (this.show_company == false) return 1
      else return 2
    },
    expired() {
      return this.survey.expires_at && new Date(this.survey.expires_at) < new Date()
    },
  },
  beforeMount() {
    // Preview Mode
    // Get survey preview data by survey token
    if (this.preview && this.$route.params.surveyToken) {
      getSurveyPreview(this.$route.params.surveyToken).then((res) => {
        this.account = res.data.survey.account
        this.survey_id = res.data.survey.id
        this.survey = res.data.survey
        this.upsertQuestionsAndAnswers(this.survey.questions)
      })
      // Normal Survey mode
      // Get survey data by recipient token
    } else {
      signInAndGetRecipient(this.recipient_token).then((res) => {
        this.survey = res.data.survey
        this.upsertQuestionsAndAnswers(this.survey.questions)
        this.upsertRecipientAndResponses(this.survey.recipients[0])
        this.account = res.data.survey.account
      })
    }
  },
  methods: {
    // use this as opposed to the store updateRecipient method because we use
    // a different api method to update the recipient
    updateRecipient(recipient) {
      patchRecipient(recipient).then((response) => this.upsertRecipient(response.data.recipient))
    },
    agreeAndContinue() {
      this.optin = true
    },
    goDone() {
      // Do nothing when in preview mode.
      if (this.preview) {
        return
      }
      patchRecipientDone(this.recipient.token).then((r) => {
        this.upsertRecipient(r.data.recipient)
      })
    },
    saveResponse(responseData) {
      if (this.preview) return

      // The question ID is needed for the client-side store, but not the backend API
      // (Responses are associated with questions through their Answer)
      delete responseData.question_id

      let action
      if (responseData.response_id) {
        responseData['id'] = responseData.response_id
        action = patchResponse(responseData)
      } else {
        action = postResponse(responseData)
      }

      action
        .then((res) => {
          this.upsertSurveyResponse(res.data.response)
        })
        .catch((e) => {
          appsignal.sendError(e, (span) => {
            span.setAction('upsertResponse')
            span.setNamespace('survey')
            span.setParams({ ...responseData })
          })
        })
    },
    removeResponse(response) {
      if (this.preview) return

      deleteResponse(response).then(() => {
        this.removeSurveyResponse(response)
      })
    },
  },
}
</script>
<style lang="sass" scoped>
.interstitial
  ::v-deep strong
    font-weight: 800
.survey-powered-by
  display: flex
  .subheading
    font-size: 12px
    font-weight: 500
    text-align: center
    margin: 0 auto 16px
    display: flex
    align-items: center

.survey-powered-logo
  display: inline-flex
  margin-left: 12px
  svg
    max-height: 24px
    max-width: 120px
    height: 100%
    width: 100%

.survey-description
  margin: 64px 40px 128px
  max-width: 700px
  font-size: 16px
  color: hsl(200, 8%, 20%)

.subtle-text-link
  color: hsl(200, 8%, 12%)
  text-decoration: underline
  text-decoration-color: hsl(200, 16%, 48%)
  &:hover
    color: var(--uePurple)
    text-decoration-color: var(--uePurple)

.ue-survey-taking-submit-btn-container
  width: 100%
  height: 40px
  top: -56px
  left: 0
  position: relative
  display: inline-block
  .btn
    position: absolute
    right: 0
    top: 0

.btn-survey-taking
  font-weight: 500
  color: white !important
  text-shadow: 0 1px 0 hsla(200, 8%, 8%, .32)
  background: linear-gradient(hsl(270, 100%, 56%), hsl(270, 100%, 48%))
  background-color: var(--uePurple)
  border: 1px solid hsl(270, 100%, 48%)
  box-shadow: 0 1px 4px 0 hsla(270, 100%, 88%, .25) inset, 0 -1px 2px 0 hsla(270, 100%, 50%, .4) inset
  padding: 11px 24px
  line-height: 1
  font-size: 16px
  border-radius: 8px
  &:hover
    color: white !important
    background: linear-gradient(hsl(270, 100%, 44%), hsl(270, 100%, 40%))
    background-color: hsl(270, 100%, 42%)
    border: 1px solid hsl(270, 100%, 40%)
  &:focus
    border: 1px solid white
    box-shadow: 0 1px 4px 0 hsla(270, 100%, 88%, .25) inset, 0 -1px 2px 0 hsla(270, 100%, 50%, .4) inset, 0 0 0 4px hsl(270, 100%, 68%)
  &:hover, &:focus
    transition: .1s all

@media (max-width: 800px)
  .survey-description
    margin: 24px 32px 64px

  .survey
    padding: 0 24px 0 32px
  .question
    h2
      margin-left: -8px
    ::v-deep
      ul
        padding-left: 0

@media (max-width: 560px)
  .survey
    margin: 48px auto 32px

  .survey_heading
    margin-left: auto

  .survey-description
    margin-left: 0
    margin-right: 0

  .questions
    margin-top: 80px

  .survey
    padding: 0 32px

  .question
    margin-top: 128px
    h2
      position: absolute
      font-size: 28px
      line-height: 28px
      top: -32px
      left: 8px

  .ue-survey-taking-submit-btn-container
    top: -64px
    .btn
      width: 100%
      left: 0
      top: 0

@media (max-width: 359px)
  .survey
    padding: 0 24px
  .company-logo img
    max-width: 176px
  h1
    font-size: 24px
  .question h2
    font-size: 24px
    line-height: 24px
    top: -24px
  .survey-description
    font-size: 14px
    margin: 24px auto
  .questions
    margin-top: 0
  .question
    margin-top: 80px
  .custom-control-label
    font-size: 13px
    padding-top: 9px
</style>
